import { Button, Label } from "@nexus/ui"
import { useState, type KeyboardEvent, type ReactNode } from "react"
import { useTranslation } from "react-i18next"

import type { VendorHandoffCheck } from "@nexus/contracts"

import { VendorHandoffChecks } from "@/features/marketplace/components/vendor-email/VendorHandoffChecks"
import { withFullWidthEmailPreview } from "@/features/marketplace/lib/vendor-email-preview"

export type VendorEmailViewMode = "desktop" | "mobile" | "plain"

const MOBILE_WIDTH_PX = 375

export function parseAddressList(value: string): string[] {
  return value
    .split(/[,;\n]+/)
    .map((part) => part.trim())
    .filter(Boolean)
}

function AddressChips({
  addresses,
  onRemove,
  disabled,
}: {
  addresses: string[]
  onRemove?: (address: string) => void
  disabled?: boolean
}) {
  const { t } = useTranslation()
  if (addresses.length === 0) return null
  return (
    <ul className="flex flex-wrap gap-1">
      {addresses.map((address) => (
        <li
          key={address}
          className="inline-flex h-6 items-center gap-1 rounded-md bg-muted px-1.5 text-[11px] text-foreground"
        >
          <span>{address}</span>
          {onRemove && !disabled ? (
            <button
              type="button"
              className="cursor-pointer text-muted-foreground hover:text-foreground"
              aria-label={t("marketplace.sendHandoff.removeRecipient", "Remove {{email}}", {
                email: address,
              })}
              onClick={() => onRemove(address)}
            >
              {t("marketplace.sendHandoff.removeRecipientMark", "×")}
            </button>
          ) : null}
        </li>
      ))}
    </ul>
  )
}

function AddressComposer({
  id,
  value,
  disabled,
  placeholder,
  onChange,
}: {
  id: string
  value: string
  disabled?: boolean
  placeholder: string
  onChange: (next: string) => void
}) {
  const addresses = parseAddressList(value)
  const [draft, setDraft] = useState("")

  function commitDraft() {
    const next = draft.trim()
    if (!next) return
    onChange([...addresses, next].join(", "))
    setDraft("")
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter" || event.key === "," || event.key === "Tab") {
      if (draft.trim()) {
        event.preventDefault()
        commitDraft()
      }
    }
    if (event.key === "Backspace" && !draft && addresses.length > 0) {
      onChange(addresses.slice(0, -1).join(", "))
    }
  }

  return (
    <div className="flex min-h-8 min-w-0 flex-1 flex-wrap items-center gap-1 rounded-sm border border-input-border bg-input-background px-1.5 py-0.5">
      <AddressChips
        addresses={addresses}
        disabled={disabled}
        onRemove={(address) => onChange(addresses.filter((item) => item !== address).join(", "))}
      />
      <input
        id={id}
        value={draft}
        disabled={disabled}
        placeholder={addresses.length === 0 ? placeholder : undefined}
        className="h-6 min-w-[7rem] flex-1 bg-transparent text-xs text-foreground outline-none placeholder:text-muted-foreground/80 disabled:cursor-not-allowed"
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commitDraft}
        onKeyDown={handleKeyDown}
      />
    </div>
  )
}

interface VendorEmailFrameProps {
  from: string | null
  replyTo: string | null
  to: string
  cc: string
  bcc?: string
  subject: string
  html: string | null
  text: string | null
  view: VendorEmailViewMode
  onViewChange: (view: VendorEmailViewMode) => void
  editable?: boolean
  disabled?: boolean
  onToChange?: (next: string) => void
  onCcChange?: (next: string) => void
  onBccChange?: (next: string) => void
  onSubjectChange?: (next: string) => void
  checks?: { blockers: VendorHandoffCheck[]; warnings: VendorHandoffCheck[] }
  editedCount?: number
  isLegacy?: boolean
  iframeTitle: string
  /** Plain copy for the To row when `to` is empty (not rendered as address chips). */
  toSummary?: string | null
  actions?: ReactNode
}

export function VendorEmailFrame({
  from,
  replyTo,
  to,
  cc,
  bcc = "",
  subject,
  html,
  text,
  view,
  onViewChange,
  editable = false,
  disabled = false,
  onToChange,
  onCcChange,
  onBccChange,
  onSubjectChange,
  checks,
  editedCount = 0,
  isLegacy = false,
  iframeTitle,
  toSummary = null,
  actions,
}: VendorEmailFrameProps) {
  const { t } = useTranslation()
  const toList = parseAddressList(to)
  const ccList = parseAddressList(cc)
  const bccList = parseAddressList(bcc)
  const warningCount = checks?.warnings.length ?? 0
  const blockerCount = checks?.blockers.length ?? 0

  return (
    <div className="flex h-full min-h-0 w-full flex-1 flex-col gap-2">
      <div className="shrink-0 space-y-1.5 rounded-md border border-border bg-muted/20 px-2.5 py-2">
        <div className="flex items-center gap-2 text-xs">
          <span className="w-12 shrink-0 text-muted-foreground">
            {t("marketplace.sendHandoff.reviewFrom", "From")}
          </span>
          <span className="min-w-0 truncate text-foreground">
            {from ?? t("marketplace.sendHandoff.reviewFromEmpty", "—")}
            {replyTo
              ? t("marketplace.sendHandoff.reviewReplyToInline", " · Reply-to {{email}}", {
                  email: replyTo,
                })
              : null}
          </span>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <Label htmlFor="vendor-email-to" className="w-12 shrink-0 text-muted-foreground">
            {t("marketplace.sendHandoff.reviewTo", "To")}
          </Label>
          {editable && onToChange ? (
            <AddressComposer
              id="vendor-email-to"
              value={to}
              disabled={disabled}
              placeholder={t("marketplace.sendHandoff.addRecipient", "Add recipient")}
              onChange={onToChange}
            />
          ) : toList.length > 0 ? (
            <AddressChips addresses={toList} />
          ) : toSummary ? (
            <span className="min-w-0 text-foreground">{toSummary}</span>
          ) : null}
        </div>
        {(editable || ccList.length > 0 || bccList.length > 0) && (
          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground">
              {ccList.length > 0 || bccList.length > 0
                ? t("marketplace.sendHandoff.reviewCcBccSummary", {
                    cc: ccList.length,
                    bcc: bccList.length,
                    defaultValue: "Cc {{cc}} · Bcc {{bcc}}",
                  })
                : t("marketplace.sendHandoff.reviewCcBcc", "Cc / Bcc")}
            </summary>
            <div className="mt-1.5 flex flex-col gap-1.5">
              <div className="flex items-center gap-2">
                <Label htmlFor="vendor-email-cc" className="w-12 shrink-0 text-muted-foreground">
                  {t("marketplace.sendHandoff.reviewCc", "Cc")}
                </Label>
                {editable && onCcChange ? (
                  <AddressComposer
                    id="vendor-email-cc"
                    value={cc}
                    disabled={disabled}
                    placeholder={t("marketplace.sendHandoff.addRecipient", "Add recipient")}
                    onChange={onCcChange}
                  />
                ) : (
                  <AddressChips addresses={ccList} />
                )}
              </div>
              {editable && onBccChange ? (
                <div className="flex items-center gap-2">
                  <Label htmlFor="vendor-email-bcc" className="w-12 shrink-0 text-muted-foreground">
                    {t("marketplace.sendHandoff.reviewBcc", "Bcc")}
                  </Label>
                  <AddressComposer
                    id="vendor-email-bcc"
                    value={bcc}
                    disabled={disabled}
                    placeholder={t("marketplace.sendHandoff.addRecipient", "Add recipient")}
                    onChange={onBccChange}
                  />
                </div>
              ) : bccList.length > 0 ? (
                <AddressChips addresses={bccList} />
              ) : null}
            </div>
          </details>
        )}
        <div className="flex items-center gap-2">
          <Label htmlFor="vendor-email-subject" className="w-12 shrink-0 text-xs text-muted-foreground">
            {t("marketplace.sendHandoff.reviewSubject", "Subject")}
          </Label>
          {editable ? (
            <input
              id="vendor-email-subject"
              value={subject}
              disabled={disabled}
              className="h-8 min-w-0 flex-1 rounded-sm border border-input-border bg-input-background px-2 text-xs font-medium text-foreground outline-none focus-visible:border-input-focus-border disabled:opacity-40"
              onChange={(event) => onSubjectChange?.(event.target.value)}
            />
          ) : (
            <p className="min-w-0 truncate text-sm font-medium text-foreground">{subject}</p>
          )}
        </div>
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-1.5">
        {(["desktop", "mobile", "plain"] as const).map((mode) => (
          <Button
            key={mode}
            type="button"
            size="sm"
            variant={view === mode ? "secondary" : "ghost"}
            className="h-7"
            onClick={() => onViewChange(mode)}
          >
            {mode === "desktop"
              ? t("marketplace.sendHandoff.viewDesktop", "Desktop")
              : mode === "mobile"
                ? t("marketplace.sendHandoff.viewMobile", "Mobile")
                : t("marketplace.sendHandoff.viewPlain", "Plain text")}
          </Button>
        ))}
        {editedCount > 0 ? (
          <span className="text-[11px] font-medium text-primary">
            {t("marketplace.sendHandoff.sectionsEdited", {
              count: editedCount,
            })}
          </span>
        ) : null}
        {isLegacy ? (
          <span className="rounded-md bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
            {t("marketplace.orders.list.brief.legacyEmail", "Rendered from stored fields")}
          </span>
        ) : null}
        {actions ? <div className="ml-auto flex flex-wrap gap-1.5">{actions}</div> : null}
      </div>

      {view === "plain" ? (
        <pre className="min-h-0 flex-1 overflow-auto whitespace-pre-wrap rounded-md border border-border bg-background p-3 text-drawer-body text-foreground">
          {text || ""}
        </pre>
      ) : html ? (
        <div className="relative min-h-[240px] w-full flex-1 overflow-hidden rounded-md border border-border bg-muted/30">
          <iframe
            title={iframeTitle}
            sandbox=""
            srcDoc={view === "desktop" ? withFullWidthEmailPreview(html) : html}
            className={
              view === "mobile"
                ? "absolute inset-y-0 left-1/2 h-full -translate-x-1/2 border-0"
                : "absolute inset-0 h-full w-full border-0"
            }
            style={view === "mobile" ? { width: MOBILE_WIDTH_PX, maxWidth: "100%" } : undefined}
          />
        </div>
      ) : (
        <p className="text-drawer-body text-muted-foreground">
          {t("marketplace.sendHandoff.previewEmpty", "The production partner email preview is loading.")}
        </p>
      )}

      {blockerCount > 0 || warningCount > 0 ? (
        <VendorHandoffChecks
          blockers={checks?.blockers ?? []}
          warnings={checks?.warnings ?? []}
        />
      ) : null}
    </div>
  )
}
